import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { showAnimation } from 'src/animations';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { Product } from '../../../classes';
import { PRODUCT_ID_PROVIDER } from '../../../interfaces';
import { ProductService } from '../../../services/product.service';
import { ProductManagementComponentService } from './product-management-component.service';

@Component({
  selector: 'app-product-management',
  templateUrl: './product-management.component.html',
  styleUrls: ['./product-management.component.css'],
  providers: [
    { provide: PRODUCT_ID_PROVIDER, useClass: ProductManagementComponentService }
  ],
  animations: [showAnimation]
})
export class ProductManagementComponent implements OnDestroy, OnInit {

  products: Product[] = [];
  currentProduct: Product = null;

  formGroup: FormGroup;

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(PRODUCT_ID_PROVIDER) private _productManagementComponentService: ProductManagementComponentService,
    private _productService: ProductService,
    private _matSnackbar: MatSnackBar,
    private _formBuilder: FormBuilder,
    private _dialog: MatDialog
  ) {
    this.formGroup = this._formBuilder.group({
      filter: this._formBuilder.control(null),
      product: this._formBuilder.group(new Product())
    });
  }

  createNew() {
    this.currentProduct = null;
    this.productControl.reset();
    this.productControl.markAsPristine();
    this._productManagementComponentService.setProductId(null);
  }

  deleteProduct(product: Product, event: MouseEvent) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this._dialog.open(DoubleCheckDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      data: {
        doubleCheckValue: (product.productnumber as any).replaceAll('.', ''),
        text: `Möchten Sie das Produkt dauerhaft löschen? Verwenden Sie den folgenden Code: ${(product.productnumber as any).replaceAll('.', '')}.`,
        mode: 'numeric',
        icon: 'delete_forever',
        title: 'Produkt löschen'
      } as DoubleCheckDialogComponentInputData
    }).afterClosed().pipe(filter(x => x ? true : false), switchMap(x => this._productService.deleteProduct(product.productid))).subscribe({
      next: () => {
        let products = this.products;
        let index = products.findIndex(x => x.productid === product.productid);
        if (index > -1) {
          products.splice(index, 1);
          this.products = products;
          this.createNew();
          this._matSnackbar.open(`Das Produkt ${product.productnumber} wurde erfolgreich gelöscht.`, 'Ok', {
            duration: 2000
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._productService.getAllProducts(true).subscribe({
      next: (products: Product[]) => {
        this.products = products;
      }
    })
  }

  save() {
    this._productService[this.currentProduct ? 'updateProduct' : 'createProduct'](this.productControl.value).subscribe({
      next: (product: Product) => {
        if (!this.currentProduct) this.products = [...this.products, product];
        else {
          let products = this.products;
          let index = products.findIndex(x => x.productid === product.productid);
          if (index > -1) {
            products[index] = product;
            this.products = products;
          }
        }
        this.setProduct(product);
        this._matSnackbar.open(`Das Produkt ${product.productnumber} wurde erfolgreich ${this.currentProduct ? 'geändert' : 'angelegt'}.`, 'Ok', {
          duration: 2000
        });
      }
    });
  }

  setProduct(product: Product) {
    this.currentProduct = product;
    this.productControl.patchValue(product);
    this.productControl.markAsPristine();
    this._productManagementComponentService.setProductId(product.productid);
  }

  get filterControl(): AbstractControl {
    return this.formGroup.controls['filter'];
  }

  get productControl(): FormGroup {
    return this.formGroup.controls['product'] as FormGroup;
  }

}
